import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Button, message, Input, Space, Tooltip, App } from 'antd';
import { ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { formatDateTime } from '../../utils/format';
import request from '../../utils/request';
import authService from '../../services/auth';

const AdminLoginLogs = () => {
  const { modal } = App.useApp();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredLogs, setFilteredLogs] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = authService.parseJwt(token);
      if (payload && payload.sub) {
        const userId = parseInt(payload.sub.split(':')[0], 10);
        setCurrentUserId(userId);
      }
    }
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const data = await request.get('/api/admin/login_logs');
      setLogs(data.entries || []);
      setFilteredLogs(data.entries || []);
    } catch (error) {
      message.error(error.error || '获取登录日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    if (searchText) {
      const filtered = logs.filter(log => 
        log.username?.toLowerCase().includes(searchText.toLowerCase()) ||
        log.ip?.includes(searchText) ||
        log.user_agent?.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredLogs(filtered);
    } else {
      setFilteredLogs(logs);
    }
  }, [searchText, logs]);

  const banUser = (userId, username) => {
    if (userId === currentUserId) {
      message.warning('不能封禁自己的账号');
      return;
    }

    modal.confirm({
      title: '确认封禁',
      icon: <ExclamationCircleOutlined />,
      content: `确定要封禁用户 "${username}" 吗？封禁后该用户将无法登录系统。`,
      okText: '确认封禁',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await request.post(`/api/admin/users/${userId}/ban`);
          message.success('用户已封禁');
          fetchLogs();
        } catch (error) {
          message.error(error.error || '封禁失败');
        }
      },
    });
  };

  const unbanUser = (userId, username) => {
    modal.confirm({
      title: '确认解封',
      icon: <ExclamationCircleOutlined />,
      content: `确定要解封用户 "${username}" 吗？解封后该用户将可以正常登录系统。`,
      okText: '确认解封',
      cancelText: '取消',
      onOk: async () => {
        try {
          await request.post(`/api/admin/users/${userId}/unban`);
          message.success('用户已解封');
          fetchLogs();
        } catch (error) {
          message.error(error.error || '解封失败');
        }
      },
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 150,
    },
    {
      title: 'User Agent',
      dataIndex: 'user_agent',
      key: 'user_agent',
      ellipsis: {
        showTitle: false,
      },
      render: (userAgent) => (
        <Tooltip title={userAgent} placement="topLeft">
          <span>{userAgent}</span>
        </Tooltip>
      ),
    },
    {
      title: '角色',
      dataIndex: 'admin',
      key: 'admin',
      width: 100,
      render: (admin) => (
        <Tag color={admin ? 'blue' : 'default'}>
          {admin ? '管理员' : '普通用户'}
        </Tag>
      ),
    },
    {
      title: '登录时间',
      dataIndex: 'inserted_at',
      key: 'inserted_at',
      width: 180,
      render: (d) => formatDateTime(d),
    },
    {
      title: '结果',
      dataIndex: 'success',
      key: 'success',
      width: 80,
      render: (success) => (
        <Tag color={success ? 'green' : 'red'}>
          {success ? '成功' : '失败'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'banned',
      key: 'banned',
      width: 80,
      render: (banned) => (
        <Tag color={banned ? 'red' : 'green'}>
          {banned ? '已封禁' : '正常'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        const isSelf = record.user_id === currentUserId;
        return (
          <>
            {record.banned ? (
              <Button
                size="small"
                type="link"
                onClick={() => unbanUser(record.user_id, record.username)}
              >
                解封
              </Button>
            ) : (
              <Tooltip title={isSelf ? '不能封禁自己' : ''}>
                <Button
                  size="small"
                  danger
                  type="link"
                  disabled={isSelf}
                  onClick={() => banUser(record.user_id, record.username)}
                >
                  封禁
                </Button>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title="用户登录日志"
        extra={
          <Space>
            <Input
              placeholder="搜索用户名、IP或User Agent"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredLogs}
          columns={columns}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default AdminLoginLogs;
